import React, { useEffect, useState } from "react";
import api from "@/api";
import { BarChart2, TrendingUp, FileText, Cpu, Layers } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const MetricCard = ({ label, value, icon: Icon, testId }) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-5">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded-md bg-[#E5F0F2] flex items-center justify-center text-[#006080]">
        <Icon size={13} />
      </div>
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
    </div>
    <p className="text-2xl font-bold text-slate-900">{value?.toLocaleString() ?? "—"}</p>
  </div>
);

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/analytics/overview"), api.get("/analytics/usage?days=30")]).then(([o, u]) => {
      setOverview(o.data);
      setUsage(u.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Analytics</h2>
        <p className="text-sm text-slate-500 mt-0.5">Document processing statistics and usage metrics.</p>
      </div>

      {/* Overview metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard label="Total Docs" value={overview?.total_documents} icon={FileText} testId="metric-total-docs" />
        <MetricCard label="Approved" value={overview?.approved} icon={TrendingUp} testId="metric-approved" />
        <MetricCard label="Pending" value={overview?.pending} icon={Layers} testId="metric-pending" />
        <MetricCard label="Pages Processed" value={overview?.total_pages_processed} icon={FileText} testId="metric-pages" />
        <MetricCard label="AI Calls" value={overview?.total_ai_calls} icon={Cpu} testId="metric-ai-calls" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Document volume chart */}
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-4">Documents (Last 7 Days)</h3>
          {overview?.chart_data?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={overview.chart_data} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                <defs>
                  <linearGradient id="colorDocs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#006080" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#006080" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 6 }} />
                <Area type="monotone" dataKey="documents" stroke="#006080" strokeWidth={2} fill="url(#colorDocs)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-sm text-slate-400">No data yet</div>
          )}
        </div>

        {/* Usage chart */}
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-4">AI Usage (Last 30 Days)</h3>
          {usage?.usage_by_day?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={usage.usage_by_day} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 6 }} />
                <Bar dataKey="ai_calls" fill="#006080" radius={[2, 2, 0, 0]} />
                <Bar dataKey="pages" fill="#E5F0F2" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-sm text-slate-400">No usage data yet</div>
          )}
        </div>
      </div>

      {/* Token usage */}
      {overview?.total_tokens > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Token Usage Summary</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-lg font-bold text-slate-900">{overview.total_tokens?.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Total Tokens</p>
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{overview.total_pages_processed?.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Pages OCR'd</p>
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{overview.schemas}</p>
              <p className="text-xs text-slate-500">Active Schemas</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
